/**
 * Advice intensity picker — incremental → moderate → ambitious.
 * Radical options show risk + advisor escalation when required.
 */
import React, { useMemo, useState } from 'react';
import { ExclamationTriangleIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import TalkToAdvisorButton from '../advice/TalkToAdvisorButton';

const INTENSITY_ORDER = ['incremental', 'moderate', 'ambitious'];

const DEFAULT_LABELS = {
  incremental: 'Incremental',
  moderate: 'Moderate',
  ambitious: 'Ambitious',
};

const DEFAULT_DESCRIPTIONS = {
  incremental: 'Low-risk, do-today moves.',
  moderate: 'Meaningful reallocation with grounded numbers.',
  ambitious: 'Large structural moves — grounded, with human confirmation when needed.',
};

const formatPayloadPreview = (payload) => {
  if (!payload?.verdict) return null;
  const parts = [payload.verdict];
  if (payload.number) parts.push(payload.number);
  if (payload.act_consequence) parts.push(payload.act_consequence);
  if (payload.inaction_consequence) parts.push(payload.inaction_consequence);
  if (payload.next_step_24h) parts.push(`Next 24 hours: ${payload.next_step_24h}`);
  return parts.join('\n\n');
};

const VisionIntensitySpectrum = ({
  spectrum = {},
  spectrumMeta = {},
  selectedIntensity = 'incremental',
  onSelectIntensity,
  visionId = null,
  className = '',
}) => {
  const [active, setActive] = useState(selectedIntensity || 'incremental');

  const labels = spectrumMeta?.labels || DEFAULT_LABELS;
  const descriptions = spectrumMeta?.descriptions || DEFAULT_DESCRIPTIONS;
  const allowed = spectrumMeta?.allowed_intensities || INTENSITY_ORDER;

  const tabs = useMemo(
    () =>
      INTENSITY_ORDER.map((key) => ({
        key,
        label: labels[key] || DEFAULT_LABELS[key],
        description: descriptions[key] || DEFAULT_DESCRIPTIONS[key],
        payload: spectrum[key],
        allowed: allowed.includes(key),
        suppressed: spectrum[key]?.suppressed || (key === 'ambitious' && spectrumMeta?.ambitious_suppressed),
      })),
    [spectrum, spectrumMeta, labels, descriptions, allowed],
  );

  const handleSelect = (key) => {
    if (!allowed.includes(key)) return;
    setActive(key);
    onSelectIntensity?.(key, spectrum[key]);
  };

  const activeTab = tabs.find((t) => t.key === active) || tabs[0];
  const activePayload = activeTab?.payload;
  const needsAdvisor = activePayload?.escalation_trigger === 'route_to_advisor';
  const needsConfirm = activePayload?.escalation_trigger === 'confirm';

  if (!spectrum || Object.keys(spectrum).length === 0) return null;

  return (
    <div className={`mt-4 rounded-lg border border-hairline bg-pine/5 p-4 ${className}`}>
      <p className="text-label text-secondary mb-1">Choose your ambition level</p>
      <p className="text-body text-primary mb-3">
        Aura can suggest small safe moves or bigger structural changes — pick what fits you.
      </p>

      {spectrumMeta?.ambitious_suppressed && spectrumMeta?.ambitious_suppressed_reason && (
        <div className="flex gap-2 items-start rounded-md bg-amber-50 border border-amber-200/80 px-3 py-2 mb-3">
          <ExclamationTriangleIcon className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
          <p className="text-label text-amber-900">{spectrumMeta.ambitious_suppressed_reason}</p>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        {tabs.map((tab) => {
          const isActive = active === tab.key;
          const disabled = !tab.allowed || tab.suppressed;
          return (
            <button
              key={tab.key}
              type="button"
              disabled={disabled}
              onClick={() => handleSelect(tab.key)}
              className={`px-3 py-1.5 rounded-md text-label transition-colors border ${
                isActive
                  ? 'bg-pine text-primary border-pine'
                  : disabled
                    ? 'opacity-40 cursor-not-allowed border-hairline text-secondary'
                    : 'border-hairline text-secondary hover:border-pine/50 hover:text-primary'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab && (
        <div className="space-y-2">
          <p className="text-label text-secondary">{activeTab.description}</p>
          {activeTab.suppressed ? (
            <p className="text-body text-primary">{activePayload?.verdict}</p>
          ) : (
            <p className="text-body text-primary whitespace-pre-line">
              {formatPayloadPreview(activePayload)}
            </p>
          )}

          {needsAdvisor && !activeTab.suppressed && (
            <div className="flex gap-2 items-start rounded-md border border-pine/30 bg-white/60 px-3 py-2 mt-2">
              <ShieldCheckIcon className="w-4 h-4 text-pine shrink-0 mt-0.5" />
              <div>
                <p className="text-label text-primary font-medium">Confirm with a human</p>
                <p className="text-label text-secondary">
                  This move touches investment, tax, or retirement territory. Review with a verified
                  advisor before acting.
                </p>
                <TalkToAdvisorButton
                  userQuestion={activePayload?.verdict}
                  surface="vision"
                  framing={activePayload?.verdict}
                  advicePayload={activePayload}
                  compact
                  className="mt-2"
                />
              </div>
            </div>
          )}

          {needsConfirm && !needsAdvisor && !activeTab.suppressed && (
            <p className="text-label text-secondary mt-1">
              Moderate product or tax impact — confirm details before you act.
            </p>
          )}

          {activePayload?.depth_tier >= 2 && !activeTab.suppressed && (
            <p className="text-label text-ink-faint">
              Tier {activePayload.depth_tier} advice · grounded in your numbers
              {activePayload.data_confidence ? ` · ${activePayload.data_confidence} confidence` : ''}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default VisionIntensitySpectrum;
